import { Copy, XCircle } from "lucide-react";

import { EditingBadge } from "../../components/common/EditingBadge";
import { TablePagination } from "../../components/common/TablePagination";
import { StatusBadge } from "../../components/common/StatusBadge";
import { syncEngine } from "../../lib/sync/syncEngine";
import { SortHeaderButton, compactCustomerName, formatDateTime, formatDistance, formatMoney, loadShipmentDirections, loadShipmentIds, originalRouteLabel } from "./loadsPageUtils";

export const LoadsTableSection = ({ currentPage, customerLookupById, filters, handleDelete, loading, loads, lookups, openDispatchBoard, openEditDialog, openRepeatDialog, pageSize, setCurrentPage, setPageSize, toggleSort, totalLoads }) => (
  <section className="table-shell" data-testid="loads-table-section">
    <div className="overflow-x-auto">
      <table className="data-table" data-testid="loads-table">
        <thead>
          <tr>
            <th><SortHeaderButton filters={filters} label="Load #" onToggle={toggleSort} sortKey="load_number" testId="loads-sort-header-load_number" /></th>
            <th><SortHeaderButton filters={filters} label="Customer" onToggle={toggleSort} sortKey="customer" testId="loads-sort-header-customer" /></th>
            <th>Pickup</th>
            <th>Delivery</th>
            <th><SortHeaderButton filters={filters} label="Planned picked" onToggle={toggleSort} sortKey="pickup_datetime" testId="loads-sort-header-picked" /></th>
            <th><SortHeaderButton filters={filters} label="Planned delivered" onToggle={toggleSort} sortKey="delivery_datetime" testId="loads-sort-header-delivered" /></th>
            <th>Assignment</th>
            <th>Revenue</th>
            <th>Distance</th>
            <th><SortHeaderButton filters={filters} label="Status" onToggle={toggleSort} sortKey="status" testId="loads-sort-header-status" /></th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className="py-10 text-center text-sm text-zinc-500" colSpan={11} data-testid="loads-loading-state">Loading loads...</td>
            </tr>
          ) : totalLoads ? (
            loads.map((load) => {
              const rowCustomer = customerLookupById.get(load.customer_id);
              const isCanceled = load.status === "Canceled";
              return (
                <tr className="cursor-pointer transition hover:bg-zinc-50" key={load.id} data-testid={`load-row-${load.id}`} onClick={() => openEditDialog(load)}>
                  <td>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-zinc-900">{load.load_number}</p>
                      <EditingBadge editors={syncEngine.getEditors("load", load.id)} testId={`load-row-editing-${load.id}`} />
                    </div>
                    <p className="mt-1 text-xs text-zinc-500" data-testid={`load-shipment-id-${load.id}`}>Shipment {loadShipmentIds(load)} • {loadShipmentDirections(load)}</p>
                    <p className="mt-1 text-xs text-zinc-500" data-testid={`load-round-trip-flag-${load.id}`}>Round trip: {load.is_round_trip ? "Yes" : "No"}</p>
                    <p className="mt-1 text-xs text-zinc-500">Ref {load.customer_reference_number || "—"} • {load.stop_count || 0} stops</p>
                  </td>
                  <td>
                    <p data-testid={`load-customer-name-${load.id}`}>{compactCustomerName(load.customer_name, rowCustomer?.billing_address)}</p>
                    <p className="mt-1 text-xs text-zinc-500" data-testid={`load-original-route-${load.id}`}>Original route: {originalRouteLabel(load)}</p>
                  </td>
                  <td>
                    <p>{load.pickup_company}</p>
                    <p className="mt-1 text-xs text-zinc-500">{load.pickup_address}</p>
                  </td>
                  <td>
                    <p>{load.delivery_company}</p>
                    <p className="mt-1 text-xs text-zinc-500">{load.delivery_address}</p>
                  </td>
                  <td>{formatDateTime(load.planned_picked_at || load.pickup_datetime)}</td>
                  <td>{formatDateTime(load.planned_delivered_at || load.delivery_datetime)}</td>
                  <td>
                    <p>{load.combined_assignment || "No leg assignment yet"}</p>
                    {load.assignment_change_count > 1 ? <p className="mt-1 text-xs text-zinc-500">{load.assignment_change_count} route assignments</p> : null}
                  </td>
                  <td>{formatMoney(load.rate, load.currency)}</td>
                  <td data-testid={`load-distance-${load.id}`}>{formatDistance(load.distance_miles, lookups.distance_unit)}</td>
                  <td><StatusBadge status={load.dispatch_status || load.status} testId={`load-status-${load.id}`} /></td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50" data-testid={`load-dispatch-${load.id}`} disabled={isCanceled} onClick={(event) => { event.stopPropagation(); openDispatchBoard(load); }} type="button">Dispatch</button>
                      <button className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid={`load-repeat-${load.id}`} onClick={(event) => { event.stopPropagation(); openRepeatDialog(load); }} type="button"><Copy className="h-3.5 w-3.5" /> Repeat</button>
                      <button className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50" data-testid={`load-delete-${load.id}`} disabled={isCanceled} onClick={(event) => { event.stopPropagation(); handleDelete(load); }} type="button"><XCircle className="h-3.5 w-3.5" /> {isCanceled ? "Canceled" : "Cancel load"}</button>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td className="py-10 text-center text-sm text-zinc-500" colSpan={11} data-testid="loads-empty-state">No loads match the current filters.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <TablePagination currentPage={currentPage} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} testIdPrefix="loads-table" totalItems={totalLoads} />
  </section>
);